import { __css, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { useRef, useState, useCallback } from 'react';

import type { Session } from '../api/session';
import { useCanvasRenderer } from '../hooks/useCanvasRenderer';
import { useInputForwarding } from '../hooks/useInputForwarding';
import { useVideoDecoder } from '../hooks/useVideoDecoder';
import { useWebSocketTransport } from '../hooks/useWebSocketTransport';

const useStyles = __css({"root":{"xawz":0,"Bh6795r":0,"Bnnss6s":0,"fkmc3a":"fz7g6wx","a9b677":"fly5x3f","sshi5w":"fprs0cq","mc9l5x":"f22iagw","Brf1p80":"f4d9j23","Bt984gj":"f122n59","De3pzq":"f1dyx4of","B68tc82":0,"Bmxbyg5":0,"Bpg54ce":"f1a3p1vp"},"canvas":{"a9b677":"fly5x3f","Bqenvij":"f1l02sjl","st4lth":"f1kle4es"},"skeleton":{"a9b677":"fly5x3f","Bqenvij":"f1l02sjl","mc9l5x":"f22iagw"},"skeletonItem":{"a9b677":"fly5x3f","Bqenvij":"f1l02sjl"}});

export interface StreamMessage {
  type: string;
  [key: string]: unknown;
}

interface VideoStreamProps {
  session: Session;
  onMessage?: (message: StreamMessage) => void;
}

const VideoStream = ({ session, onMessage }: VideoStreamProps) => {
  const styles = useStyles();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState<string | null>(null);

  // Canvas rendering abstraction
  const { drawFrame, setCanvasSize } = useCanvasRenderer(canvasRef);

  // Video decoder abstraction
  const { decode, reset: resetDecoder } = useVideoDecoder({
    onFrame: drawFrame,
    onDecoderError: setError,
  });

  // Handle json messages and video size changes
  const handleJsonMessages = useCallback(
    (messages: unknown[]) => {
      for (const msg of messages) {
        if (
          msg &&
          typeof msg === 'object' &&
          'type' in msg &&
          typeof msg.type === 'string'
        ) {
          const streamMsg = msg as StreamMessage;
          if (onMessage) {
            onMessage(streamMsg);
          }
          if (
            streamMsg.type === 'videoSizeChanged' &&
            'width' in streamMsg &&
            'height' in streamMsg
          ) {
            const width = Number(streamMsg.width);
            const height = Number(streamMsg.height);
            if (
              Number.isFinite(width) &&
              width > 0 &&
              Number.isFinite(height) &&
              height > 0
            ) {
              setCanvasSize(width, height);
            }
          }
        }
      }
    },
    [onMessage, setCanvasSize],
  );

  // WebSocket transport + Reconnect abstraction
  const { isConnected } = useWebSocketTransport({
    sessionId: session.id,
    activeWindowId: session.windows[0],
    seastarLocation: session.properties?.seastarLocation,
    onBinaryMessage: decode,
    onJsonMessages: handleJsonMessages,
    onBeforeReconnect: resetDecoder,
  });

  useInputForwarding({
    canvasRef,
    session,
    isConnected,
  });

  if (error) {
    return (
      <div className={styles.root}>
        <span style={{ color: 'white' }}>{error}</span>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {!isConnected && (
        <Skeleton className={styles.skeleton} aria-label="Loading video stream">
          <SkeletonItem className={styles.skeletonItem} />
        </Skeleton>
      )}
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        style={{ display: isConnected ? 'block' : 'none' }}
      />
    </div>
  );
};

export default VideoStream;


import "/usr/local/jenkins/workspace/BI_bi-frontend_2.2.3/src/components/VideoStream.griffel.css";